import Error from 'components/form/Error';
import Input from 'components/form/Inputs/Input';
import EditIcon from 'components/icons/Edit';
import TrashIcon from 'components/icons/Trash';
import ActionModal from 'components/Modal/ActionModal';
import { FC, useCallback, useState } from 'react';
import styled from 'styled-components';
import { mutate } from 'swr';
import MentorshipReferee from 'types/MentorshipReferee';
import privateAPI from 'utils/privateAxiosInstance';
import { Cell } from '.';

const Icons = styled.div`
  display: flex;
  align-items: center;
  div {
    stroke: var(--primary-600);
    height: 21px;
    padding: 1px;
    margin-left: 0.7rem;
    cursor: pointer;
  }

  svg {
    height: 100%;
  }

  div:last-child {
    stroke: var(--error-600);
  }
`;

type Props = {
  referee: MentorshipReferee;
};
const Referee: FC<Props> = ({ referee }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [name, setName] = useState<string>(referee.name || '');
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setName(e.target.value);
  };
  const close = useCallback(() => {
    setOpen(false);
    setName(referee.name || '');
  }, [referee]);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<'general' | 'name' | null>(null);
  const handleUpdate = () => {
    setLoading(true);
    setError(null);
    privateAPI
      .post(`/mentorship-referee/${referee.id}`, { name })
      .then(() => {
        setOpen(false);
        mutate('/mentorship-referee');
      })
      .catch((err) => {
        if (
          err?.response?.data?.fields?.length &&
          err.response.data.fields.includes('name')
        ) {
          return setError('name');
        }
        setError('general');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<boolean>(false);
  const handleDelete = () => {
    setDeleteLoading(true);
    setDeleteError(false);
    privateAPI
      .delete(`/mentorship-referee/${referee.id}`)
      .then(() => {
        setDeleteOpen(false);
        mutate('/mentorship-referee');
      })
      .catch(() => {
        setDeleteError(true);
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };
  return (
    <>
      <Cell>
        {referee.name}
        <Icons>
          <div onClick={() => setOpen(true)}>
            <EditIcon />
          </div>
          <div onClick={() => setDeleteOpen(true)}>
            <TrashIcon />
          </div>
        </Icons>
      </Cell>
      <ActionModal
        open={open}
        close={close}
        color="primary"
        title="Modifier un chargé de mentorat"
        apply={handleUpdate}
        applyText="Enregistrer"
        cancel={close}
        loading={loading}
      >
        {error && (
          <Error style={{ marginBottom: '0.5rem' }}>
            {error === 'general'
              ? 'Une erreur est survenue.'
              : 'Ce nom est déjà utilisé.'}
          </Error>
        )}
        <Input value={name} onChange={handleChange} />
      </ActionModal>
      <ActionModal
        open={deleteOpen}
        close={() => setDeleteOpen(false)}
        color="error"
        title="Supprimer un chargé de mentorat"
        apply={handleDelete}
        applyText="Supprimer définitivement"
        loading={deleteLoading}
      >
        {deleteError && (
          <Error style={{ marginBottom: '0.5rem' }}>
            Une erreur est survenue.
          </Error>
        )}
        <p>
          Vous êtes sur le point de supprimer ce chargé de mentorat:{' '}
          <b style={{ fontWeight: 500 }}>{referee.name}</b>
        </p>
        <p style={{ marginTop: '1rem' }}>
          Tous les couples mentor/mentoré auxquels ce chargé de mentorat est
          attaché n'auront plus de chargé de mentorat. Il faudra alors leur en
          attacher un nouveau manuellement.
        </p>
        <p>
          En cas de changement de personnel, privilégiez plutôt un changement de
          nom.
        </p>
        <p style={{ marginTop: '1rem' }}>
          Souhaitez-vous supprimer définitivement ce chargé de mentorat ?
        </p>
      </ActionModal>
    </>
  );
};

export default Referee;
